import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, type ApiClient } from '../../api/client';
import type { ObservationAgreementDocument } from '../../api/types';
import { ClinicField } from '../../components/ClinicField';
import { Modal } from '../../components/Modal';
import { ReadAloud } from '../../components/ReadAloud';
import { useClinicDirectory } from '../../hooks/useClinicDirectory';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { AgreementText } from '../../observation-agreement/AgreementText';

/*
 * The wording the page prints and the wording it reads aloud, kept in one place so they cannot drift
 * apart. The agreement itself comes from the API and is spoken from the same response the page
 * renders; these are the page's own — the three boxes above the text, and what signing commits to.
 */
const signerNameLabel = 'Your full name';
const signerNameHint = 'This is your signature. Type it as you would sign it.';
const emailLabel = 'Your email address';
const emailHint = 'So the clinic can reach you about your visit.';
const clinicLabel = 'Clinic you are observing at';
const signLabel = 'Sign the agreement';
const signedWarning = 'Today’s date is recorded with your signature. You cannot change it afterwards.';

/**
 * The Chiropractic Office Observation Agreement, as the person observing signs it.
 *
 * Public: no sign-in, no hub shell, no sidebar — see `PublicApp`. The agreement comes from
 * `GET /api/public/observation-agreement/form` rather than living here, so HR can have the text
 * revised without a frontend change.
 *
 * The paper form has three hand-written boxes — signature, printed name, date. Here there are none
 * of them: the typed name is both signature and printed name, since a typed name is the same
 * characters twice, and the date is the moment the API accepts it, which is the one value on the
 * page nobody should be able to write themselves.
 *
 * Nothing is kept in `localStorage`, same as the CT assessment and for the same reason: this runs on
 * a shared front-desk machine, and one person's half-filled agreement must not be sitting there for
 * whoever sits down next. There is no `beforeunload` warning either, which the assessment does have
 * — three short fields are a few seconds to retype, and a browser prompt on the way out of a page
 * somebody has decided to leave is worth less than the interruption costs.
 */
export function ObservationAgreementFormPage({ api }: { api: ApiClient }) {
  const [agreement, setAgreement] = useState<ObservationAgreementDocument | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [signerName, setSignerName] = useState('');
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');
  const [agreed, setAgreed] = useState(false);

  // The clinic directory the third box is filled from, on its own call and with its own failure: an
  // unreachable directory costs that one field its list, never the agreement. See useClinicDirectory
  // and ClinicField for what the signer gets instead.
  const loadClinics = useCallback((signal: AbortSignal) => api.listPublicClinics(signal), [api]);
  const clinics = useClinicDirectory(loadClinics);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  /** Set once the API has recorded the signature; from then on this page is the thank-you screen. */
  const [signed, setSigned] = useState(false);

  /** The banner above the form, focused when a signing is turned away — see `failSubmit` below. */
  const errorRef = useRef<HTMLParagraphElement>(null);

  useDocumentTitle(agreement ? `${agreement.title} — TVG-Medulla` : 'Loading the agreement — TVG-Medulla');

  /**
   * How many times the banner has been raised, used as its key so that each refusal mounts a fresh
   * one. See `failSubmit`.
   */
  const [errorAttempt, setErrorAttempt] = useState(0);

  /*
   * Moving focus to the error, not just the scrollbar.
   *
   * `window.scrollTo` in `failSubmit` brings the banner on screen, which does nothing for anyone not
   * watching it: press "Sign the agreement", hear nothing, still be at the bottom of a page that has
   * refused you without saying so. Focus carries the reading point, so it goes to the message.
   *
   * Keyed on the attempt rather than on the text, because neither of the two mechanisms here fires
   * on its own when the same banner is raised twice: `focus()` on the element that already holds
   * focus does nothing, and a live region whose text has not changed announces nothing. Press "Sign
   * the agreement" twice with the same field still blank and the second press was silent — worse,
   * press it once without the tick and once without the name, and the second was silent too, because
   * the banner element survived the change and simply swapped its text. The key makes every refusal
   * a new element: a new live region to announce, and an unfocused one to focus.
   */
  useEffect(() => {
    if (submitError) errorRef.current?.focus();
  }, [submitError, errorAttempt]);

  /**
   * Refuse the submission: say why, put the reason on screen, and send the reading point to it.
   *
   * Every failure path goes through here so that none of them can forget one of the three — the
   * scroll without the focus is the form that fails silently for a screen reader, and it was the
   * shape all of these had.
   */
  const failSubmit = useCallback((message: string) => {
    setSubmitError(message);
    setErrorAttempt((attempt) => attempt + 1);
    window.scrollTo({ top: 0 });
  }, []);

  /**
   * The agreement as one piece of spoken text, for the "read this page aloud" control.
   *
   * This is the page where reading it out matters most: it is the one document on the public side
   * that somebody is asked to read in full and then be bound by, and "I did not understand what I
   * signed" is the objection the whole page exists to answer.
   *
   * The blank for the name is the one place this parts company with what is on screen. `AgreementText`
   * fills it with a rule of two dozen underscores, which is right to look at and is read aloud as
   * two dozen underscores — so here it becomes the word "blank" until a name has been typed, and
   * the name itself once there is one.
   */
  const spokenPage = useMemo(() => {
    if (!agreement) return '';

    const name = signerName.trim();

    return [
      agreement.title,

      // The three boxes above the agreement, counted off as a sentence rather than read as three
      // loose labels — the count is what lets somebody listening match them to what is on screen.
      // Which must be filled in is said here because that is the question the boxes raise, and on
      // screen it is answered by an asterisk and a "(optional)" aside — two visual marks that carry
      // no weight at all when the page is read out.
      `This form has three boxes to fill in at the top. The first is ${signerNameLabel} — ` +
        `${signerNameHint} The second is ${emailLabel}. Both are needed before you can sign. ` +
        `The third is ${clinicLabel}, which is optional and is ` +
        `${clinics.state === 'unavailable' ? 'typed in' : 'chosen from a list'}.`,

      ...agreement.clauses.map((clause) =>
        // split/join, matching withName in AgreementText: the placeholder is written with braces and
        // a regular expression would read those as a repetition count.
        clause.text.split(agreement.namePlaceholder).join(name.length > 0 ? name : 'blank'),
      ),
      agreement.consentStatement,

      // Both halves of what signing does, in the order the page asks for them. The date is the one
      // value on the page nobody types, so hearing it named is the only way it is known about.
      `When you have read the agreement, tick the box to agree, then choose ${signLabel} at the ` +
        `bottom of the page. ${signedWarning}`,
    ].join('\n');
  }, [agreement, signerName, clinics.state]);

  useEffect(() => {
    const controller = new AbortController();

    api
      .getObservationAgreement(controller.signal)
      .then(setAgreement)
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;

        // The signer gets one sentence they can act on; the detail goes to the console for whoever
        // is standing next to them trying to work out why the page is blank.
        console.error('Could not load the observation agreement', error);
        setLoadError('The agreement could not be loaded. Check the connection and reload the page.');
      });

    return () => controller.abort();
  }, [api]);

  const sign = useCallback(async () => {
    if (!agreement) return;

    // The dialog stays up for the duration and closes in the `finally` below, so the button it was
    // clicked on is what shows the request running.
    setSubmitting(true);
    setSubmitError(null);
    setFieldErrors({});

    try {
      await api.signObservationAgreement({
        signerName: signerName.trim(),
        email: email.trim(),
        location: location.trim(),
        agreementVersion: agreement.version,
        agreed,
      });

      setSigned(true);
      window.scrollTo({ top: 0 });
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 409) {
        // The text was revised while this page was open, so what is on screen is not what the API
        // would record. The agreement is reloaded and the tick cleared: consent is to a text, and
        // this is a different text now, however little of it changed.
        setAgreed(false);
        failSubmit(
          'The agreement has been updated since this page was opened. The new version is now shown — ' +
            'please read it and agree again.',
        );

        try {
          setAgreement(await api.getObservationAgreement());
        } catch (reloadError: unknown) {
          console.error('Could not reload the observation agreement', reloadError);
          failSubmit(
            'The agreement has been updated since this page was opened. Reload the page to read the ' +
              'current version.',
          );
        }
      } else if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors);
        failSubmit(
          Object.keys(error.fieldErrors).length > 0
            ? 'Check the highlighted fields and sign again.'
            : error.status === 429
              ? 'Too many signatures from this computer just now. Wait a minute and try again.'
              : error.message,
        );
      } else {
        failSubmit('Your agreement could not be sent. Check the connection and try again.');
      }
    } finally {
      setSubmitting(false);

      // Either way: on success the page swaps to the thank-you screen, and on failure the message
      // belongs on the form behind this dialog, not inside it.
      setConfirmOpen(false);
    }
  }, [api, agreement, signerName, email, location, agreed, failSubmit]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    // Name and email are both required — the name is what the agreement binds, and the email is the
    // only way back to whoever signed it. The clinic is the one box the paper form's absence of it
    // still excuses.
    if (signerName.trim().length === 0) {
      setFieldErrors({ signerName: ['Enter your full name.'] });
      failSubmit('Enter your full name before signing.');
      return;
    }

    // Empty only. Whether an address is well-formed is the API's question — see LooksLikeEmail there
    // — and a second opinion here is how the two would come to disagree about an address somebody is
    // standing at a front desk trying to type.
    if (email.trim().length === 0) {
      setFieldErrors({ email: ['Enter your email address.'] });
      failSubmit('Enter your email address before signing.');
      return;
    }

    // The tick is checked here, where it used to be enforced by the button carrying `disabled` until
    // the box was ticked. That read well on screen and was a dead end off it: a disabled button is
    // out of the tab order, so a keyboard user tabbing down the page ran out of form without ever
    // reaching the control that would have told them what was left, and the line underneath it —
    // which was the whole explanation — is not something focus can land on either. The button now
    // says `aria-disabled` instead. It looks the same, keeps its place in the tab order, carries that
    // line as its description, and the press lands here and is turned away with a reason.
    if (!agreed) {
      setFieldErrors({ agreed: ['Tick the box to agree before signing.'] });
      failSubmit('Tick the box to agree to the terms before signing.');
      return;
    }

    // Always confirmed. Signing is the one irreversible thing anyone does on this page — there is no
    // edit and no withdrawal afterwards — and a button that does it on the first click is a trap
    // next to a Tab key.
    setConfirmOpen(true);
  }

  if (loadError) {
    return (
      <PublicPage>
        <div className="public-card public-card--message">
          <h1 className="public-card__title">Something went wrong</h1>
          <p>{loadError}</p>
        </div>
      </PublicPage>
    );
  }

  if (!agreement) {
    return (
      <PublicPage>
        <div className="public-card public-card--message">
          <p>Loading the agreement…</p>
        </div>
      </PublicPage>
    );
  }

  // The whole screen, deliberately: they have signed and are done. No copy is offered here, matching
  // the CT assessment — the anonymous half has no read endpoint, so a copy would exist on this one
  // screen and nowhere else, and closing the tab would be a way to lose it. The copy that matters is
  // the one recruiting exports from the dashboard.
  if (signed) {
    return (
      <PublicPage>
        <div className="public-card public-card--message">
          <h1 className="public-card__title">Thank you — your agreement has been signed.</h1>
          <p>
            It has been recorded in {signerName.trim()}&rsquo;s name and sent to the recruiting team.
            You can close this page.
          </p>
        </div>
      </PublicPage>
    );
  }

  return (
    <PublicPage>
      <form className="public-card agreement" onSubmit={handleSubmit} noValidate>
        <header className="agreement__header">
          <p className="agreement__org">{agreement.organization}</p>
          <h1 className="public-card__title">{agreement.title}</h1>
        </header>

        {/* At the top, before anything there is to read — including the name fields, since the name
            is what fills the blank in the first clause and someone may want to hear the agreement
            before deciding to put it there. */}
        <ReadAloud text={spokenPage} />

        {submitError && (
          <p key={errorAttempt} className="agreement__error" role="alert" tabIndex={-1} ref={errorRef}>
            {submitError}
          </p>
        )}

        {/* Above the agreement rather than under it, because the name fills the blank in its first
            sentence — the page reads as the paper form does only once there is a name to put there. */}
        <div className="agreement__identity">
          <TextField
            id="signerName"
            label={signerNameLabel}
            value={signerName}
            maxLength={agreement.signerNameMaxLength}
            errors={fieldErrors.signerName}
            autoComplete="name"
            hint={signerNameHint}
            onChange={setSignerName}
          />
          <TextField
            id="email"
            label={emailLabel}
            type="email"
            value={email}
            maxLength={agreement.emailMaxLength}
            errors={fieldErrors.email}
            autoComplete="email"
            hint={emailHint}
            onChange={setEmail}
          />
          <ClinicField
            id="location"
            label={clinicLabel}
            optional
            clinics={clinics}
            value={location}
            maxLength={agreement.locationMaxLength}
            errors={fieldErrors.location}
            onChange={setLocation}
          />
        </div>

        <AgreementText
          clauses={agreement.clauses}
          namePlaceholder={agreement.namePlaceholder}
          signerName={signerName.trim()}
        />

        <p className="agreement__attribution">{agreement.attribution}</p>

        <div className="agreement__consent">
          <label className="agreement__consent-label" htmlFor="agreed">
            <input
              id="agreed"
              type="checkbox"
              className="agreement__checkbox"
              checked={agreed}
              required
              aria-invalid={(fieldErrors.agreed?.length ?? 0) > 0}
              aria-describedby={fieldErrors.agreed ? 'agreed-error' : undefined}
              onChange={(event) => setAgreed(event.target.checked)}
            />
            <span>{agreement.consentStatement}</span>
          </label>

          {fieldErrors.agreed && (
            <p className="field__error" id="agreed-error">
              {fieldErrors.agreed.join(' ')}
            </p>
          )}
        </div>

        <div className="agreement__actions">
          {/* Spent-looking until the box is ticked, so the order is the one the page intends: read,
              then agree, then sign. `aria-disabled` rather than `disabled` for the unticked case —
              see the matching note in handleSubmit — which keeps it reachable and lets the line
              underneath travel with it as its description instead of sitting next to a control
              nobody can land on. `disabled` is still the right answer for the request in flight,
              which is nobody's business for the second it lasts. */}
          <button
            type="submit"
            className="button button--primary button--wide"
            disabled={submitting}
            aria-disabled={!agreed}
            aria-describedby="sign-hint"
          >
            {submitting ? 'Signing…' : signLabel}
          </button>
          <p className="agreement__hint" id="sign-hint">
            {agreed ? signedWarning : 'Tick the box above to sign.'}
          </p>
        </div>
      </form>

      <Modal
        open={confirmOpen}
        title="Sign this agreement?"
        onClose={() => setConfirmOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="button button--quiet"
              disabled={submitting}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={submitting}
              onClick={() => void sign()}
            >
              {submitting ? 'Signing…' : 'Sign'}
            </button>
          </>
        }
      >
        <p className="agreement__confirm-lead">
          You are signing the {agreement.title} as <strong>{signerName.trim()}</strong>.
        </p>
        <p className="agreement__confirm-lead">
          This has the same effect as signing it on paper, and it cannot be changed or withdrawn
          afterwards.
        </p>
      </Modal>
    </PublicPage>
  );
}

/** The plain, chrome-free frame every public page sits in. */
function PublicPage({ children }: { children: React.ReactNode }) {
  return <main className="public-page">{children}</main>;
}

function TextField({
  id,
  label,
  value,
  maxLength,
  errors,
  hint,
  type,
  optional,
  autoComplete,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  maxLength: number;
  errors?: string[];
  hint?: string;
  type?: 'text' | 'email';

  /** Marked in words rather than by the absence of an asterisk, which nobody reads as a signal. */
  optional?: boolean;
  autoComplete?: string;
  onChange: (value: string) => void;
}) {
  const invalid = (errors?.length ?? 0) > 0;
  const describedBy = [invalid ? `${id}-error` : null, hint ? `${id}-hint` : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {optional ? (
          <span className="field__optional">(optional)</span>
        ) : (
          <span className="field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <input
        id={id}
        className={invalid ? 'control control--invalid' : 'control'}
        type={type ?? 'text'}
        value={value}
        maxLength={maxLength}
        required={!optional}
        autoComplete={autoComplete}
        aria-invalid={invalid}
        aria-describedby={describedBy.length > 0 ? describedBy : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {/*
        * Both messages in one box, because the grid above counts children.
        *
        * `.field` is a subgrid of exactly three rows — label, control, message — so that a row of
        * fields keeps its labels and its inputs on the same two lines however one of them wraps. A
        * field that hands over four parts has nowhere to put the fourth: it lands in an implicit row
        * the parent never sized, which is drawn on top of the third. On screen that was the hint and
        * the error printed through each other, one letter over the next, on the one field that has
        * both — the signer's name, when it is left blank. Grouping them is what keeps the field at
        * three parts while still showing two messages.
        */}
      {(hint || invalid) && (
        <div className="field__messages">
          {hint && (
            <p className="field__help" id={`${id}-hint`}>
              {hint}
            </p>
          )}
          {invalid && (
            <p className="field__error" id={`${id}-error`}>
              {errors?.join(' ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

